import { useEffect, useState } from "react";
import { MarketingFooter } from "@/components/marketing/MarketingFooter";
import { MarketingNav } from "@/components/marketing/MarketingNav";
import { getConfig } from "@/lib/env";
import styles from "./Status.module.css";

const POLL_MS = 30_000;

type ComponentStatus = "operational" | "down" | string;

interface HealthComponent {
  name: string;
  status: ComponentStatus;
}

interface HealthResponse {
  ok: boolean;
  stage: string;
  region: string;
  table: string | null;
  time: string;
  components?: HealthComponent[];
}

type LoadState =
  | { phase: "loading" }
  | { phase: "error" }
  | { phase: "ready"; data: HealthResponse };

function isHealthResponse(value: unknown): value is HealthResponse {
  return (
    typeof value === "object" &&
    value !== null &&
    typeof (value as HealthResponse).time === "string"
  );
}

/** A component counts as healthy only when it explicitly reports operational. */
function componentOk(c: HealthComponent): boolean {
  return c.status === "operational";
}

function prettyName(name: string): string {
  switch (name) {
    case "api":
      return "API";
    case "database":
      return "Database";
    case "media":
      return "Media storage";
    case "scheduler":
      return "Publish scheduler";
    default:
      return name.charAt(0).toUpperCase() + name.slice(1);
  }
}

function prettyStatus(status: ComponentStatus): string {
  if (status === "operational") return "Operational";
  if (status === "down") return "Down";
  return status.charAt(0).toUpperCase() + status.slice(1);
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "medium",
  });
}

export default function Status() {
  const [state, setState] = useState<LoadState>({ phase: "loading" });

  useEffect(() => {
    let cancelled = false;
    const base = getConfig().apiBaseUrl.replace(/\/$/, "");
    const url = `${base}/health`;

    async function poll() {
      try {
        const res = await fetch(url, {
          headers: { accept: "application/json" },
          // CloudFront serves /api/* with caching disabled; this is belt-and-
          // suspenders so we never read a stale cached health response.
          cache: "no-store",
        });
        if (!res.ok) throw new Error(`status ${res.status}`);
        const json: unknown = await res.json();
        if (!isHealthResponse(json)) throw new Error("malformed response");
        if (cancelled) return;
        setState({ phase: "ready", data: json });
      } catch {
        if (cancelled) return;
        // HONEST failure: never fall back to a fake "all operational".
        setState({ phase: "error" });
      }
    }

    void poll();
    const id = setInterval(() => {
      void poll();
    }, POLL_MS);

    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  return (
    <>
      <MarketingNav />

      <main className={styles.main}>
        <section className={styles.hero}>
          <div className={styles.heroInner}>
            <p className={styles.kicker}>Status</p>
            <h1 className={styles.title}>System status</h1>
            <p className={styles.lead}>
              Live reachability of the services behind Notamen. This
              page polls our public health endpoint every 30 seconds.
            </p>
          </div>
        </section>

        <section className={styles.body}>
          <div className={styles.bodyInner}>
            <StatusContent state={state} />
          </div>
        </section>
      </main>

      <MarketingFooter />
    </>
  );
}

function StatusContent({ state }: { state: LoadState }) {
  if (state.phase === "loading") {
    return (
      <div className={styles.banner} data-tone="loading" role="status">
        <span className={styles.dot} aria-hidden="true" />
        <div className={styles.bannerText}>
          <span className={styles.bannerTitle}>Checking system status…</span>
          <span className={styles.bannerSub}>Contacting the status endpoint.</span>
        </div>
      </div>
    );
  }

  if (state.phase === "error") {
    return (
      <div className={styles.banner} data-tone="down" role="alert">
        <span className={styles.dot} aria-hidden="true" />
        <div className={styles.bannerText}>
          <span className={styles.bannerTitle}>
            Unable to reach the status endpoint
          </span>
          <span className={styles.bannerSub}>
            We couldn't load system status. This may mean the status endpoint
            itself is unreachable. Retrying automatically.
          </span>
        </div>
      </div>
    );
  }

  const { data } = state;
  const components = data.components ?? [];
  const allOk = components.length > 0 && components.every(componentOk);
  // With no component array we can't honestly claim "all operational"; the
  // endpoint responded but didn't report components, so treat as degraded.
  const tone = allOk ? "ok" : "down";

  return (
    <>
      <div className={styles.banner} data-tone={tone} role="status">
        <span className={styles.dot} aria-hidden="true" />
        <div className={styles.bannerText}>
          <span className={styles.bannerTitle}>
            {allOk ? "All systems operational" : "Some systems degraded"}
          </span>
          <span className={styles.bannerSub}>
            Last checked {formatTime(data.time)}
            {data.stage && data.stage !== "unknown" ? ` · ${data.stage}` : ""}
          </span>
        </div>
      </div>

      {components.length > 0 ? (
        <ul className={styles.components}>
          {components.map((c) => {
            const okComp = componentOk(c);
            return (
              <li
                key={c.name}
                className={styles.component}
                data-tone={okComp ? "ok" : "down"}
              >
                <span className={styles.componentName}>{prettyName(c.name)}</span>
                <span className={styles.componentStatus}>
                  <span className={styles.componentDot} aria-hidden="true" />
                  {prettyStatus(c.status)}
                </span>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className={styles.note}>
          The status endpoint responded but reported no component detail.
        </p>
      )}
    </>
  );
}
